/**
 * Redwork patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const redworkPatternsPack: ToilePack = {
    slug: 'redwork-patterns',
    category: 'craft',
    name: 'Redwork patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'quilt', label: 'Quilt block', subject: 'a redwork quilt block pattern of a basket of flowers inside a square frame, single clean outlines only, sized to trace onto a nine-inch block'},
        {key: 'kitchen', label: 'Kitchen', subject: 'a redwork pattern for a tea towel, a teapot, two cups and a sprig of leaves in plain outline, with dashed lines marking where the stitches run'},
        {key: 'nursery', label: 'Nursery', subject: 'a redwork pattern of a teddy bear holding a balloon, simple outlines with no filled areas, framed by a thin scalloped border'},
    ],
    styles: [
        {key: 'transfer', label: 'Transfer', style: 'single clean outline of even weight, closed shapes and no filled areas, ready to trace', palette: 'thin black line on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'backstitch', label: 'Backstitch', style: 'the outline shown as a dashed backstitch line, each stitch the same short length', palette: 'red dashed line on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'bold', label: 'Bold outline', style: 'a thicker even outline with fewer inner details, easy to trace onto cloth', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['a rooster', 'christmas', 'teacup'],
    directionHint: 'try: a rooster',
    seo: {
        title: 'Redwork patterns: quilt block, kitchen, nursery',
        metaDescription: 'Nine redwork transfer patterns: quilt block, kitchen and nursery designs, as clean outlines ready to trace and stitch.',
        h1: 'Redwork patterns: a quilt block, a kitchen towel and a nursery',
        tagline: 'Simple line drawings ready to trace and stitch in backstitch or stem stitch.',
        intro: 'Redwork is outline embroidery in one red thread on white cloth, popular since the late nineteenth century. A transfer pattern is a line drawing you trace onto the cloth and stitch over. These nine cover a quilt block, a kitchen tea towel and a nursery. Each comes as a clean outline, a red backstitch line, or a bold outline. Type a different subject and the same outline style carries it.',
        useCases: [
            'a quilt block',
            'a tea towel',
            "a child's nursery cloth",
            'a card or sampler',
        ],
        faq: [
            {q: 'Do I have to trace the pattern first?', a: 'Yes. These are transfer patterns: trace them onto the fabric, then stitch over the lines.'},
            {q: 'Why is the backstitch style red?', a: 'Redwork is traditionally stitched in red, and the backstitch style shows the stitch line in red.'},
            {q: 'Which outline is easiest to trace?', a: 'The bold outline, with a thicker even line and fewer inner details.'},
        ],
    },
}
